import React, { useMemo } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from 'recharts';
import { Card, ScoreBar, SectionTitle, Spinner } from './UI';
import ClaudeAnalysis from './ClaudeAnalysis';

function computeFundScore(q) {
  if (!q) return 50;
  let score = 50;
  if (q.pe_ratio)        score += q.pe_ratio < 10 ? 10 : q.pe_ratio < 15 ? 5 : q.pe_ratio > 25 ? -10 : 0;
  if (q.dividend_yield)  score += q.dividend_yield > 5 ? 10 : q.dividend_yield > 2 ? 5 : 0;
  if (q.roe)             score += q.roe > 15 ? 8 : q.roe > 8 ? 4 : q.roe < 0 ? -10 : 0;
  if (q.ev_ebitda)       score += q.ev_ebitda < 6 ? 8 : q.ev_ebitda < 10 ? 3 : q.ev_ebitda > 20 ? -8 : 0;
  if (q.debt_to_equity)  score += q.debt_to_equity < 50 ? 5 : q.debt_to_equity > 200 ? -8 : 0;
  if (q.price_to_book)   score += q.price_to_book < 1.5 ? 5 : q.price_to_book > 5 ? -5 : 0;
  return Math.min(100, Math.max(0, Math.round(score)));
}

function computeMacroScore(beta, sector) {
  let score = 60;
  if (beta) {
    if (beta < 0.7) score += 8;
    else if (beta > 1.5) score -= 8;
  }
  const defensiveSectors = ['Utilities', 'Consumer Staples', 'Healthcare'];
  const cyclicalSectors  = ['Energy', 'Materials', 'Financials'];
  if (defensiveSectors.some(s => sector?.includes(s))) score += 5;
  if (cyclicalSectors.some(s => sector?.includes(s)))  score -= 3;
  return Math.min(100, Math.max(0, Math.round(score)));
}

function ratingLabel(score) {
  if (score >= 75) return { label: 'FORTE ACQUISTO', color: '#10b981', icon: '📈' };
  if (score >= 62) return { label: 'ACQUISTO MODERATO', color: '#34d399', icon: '↗' };
  if (score >= 48) return { label: 'NEUTRO / HOLD', color: '#fbbf24', icon: '◆' };
  if (score >= 35) return { label: 'VENDITA MODERATA', color: '#f97316', icon: '↘' };
  return { label: 'FORTE VENDITA', color: '#f87171', icon: '📉' };
}

export { computeFundScore, computeMacroScore };

export default function VerdictPanel({ ticker, quoteData, technicalData, newsData }) {
  const scores = useMemo(() => {
    const tech  = technicalData?.score_technical ?? 50;
    const fund  = computeFundScore(quoteData);
    const news  = newsData?.sentiment_summary?.score ?? 50;
    const macro = computeMacroScore(quoteData?.beta, quoteData?.sector);
    const composite = Math.round(tech * 0.35 + fund * 0.35 + news * 0.20 + macro * 0.10);
    return { tech, fund, news, macro, composite };
  }, [technicalData, quoteData, newsData]);

  const { label, color, icon } = ratingLabel(scores.composite);

  const techSig = technicalData?.signals || {};
  const bullish = [
    techSig.sma_20 === 'BUY' && 'Prezzo sopra SMA 20',
    techSig.sma_50 === 'BUY' && 'Prezzo sopra SMA 50',
    techSig.ema_200 === 'BUY' && 'Prezzo sopra EMA 200',
    techSig.macd === 'BUY' && 'MACD con segnale rialzista',
    techSig.trend === 'RIALZISTA' && 'Trend direzionale forte al rialzo (ADX)',
    quoteData?.dividend_yield > 4 && `Dividend yield elevato (${quoteData?.dividend_yield?.toFixed(1)}%)`,
    quoteData?.pe_ratio < 12 && `P/E attraente a ${quoteData?.pe_ratio?.toFixed(1)}x`,
    quoteData?.roe > 12 && `ROE solido al ${quoteData?.roe?.toFixed(1)}%`,
    newsData?.sentiment_summary?.global === 'positivo' && 'Sentiment delle notizie positivo',
  ].filter(Boolean);

  const bearish = [
    techSig.rsi === 'IPERCOMPRATO' && `RSI in zona ipercomprata (${technicalData?.indicators?.rsi_14?.toFixed(1)})`,
    techSig.sma_20 === 'SELL' && 'Prezzo sotto SMA 20',
    techSig.ema_200 === 'SELL' && 'Prezzo sotto EMA 200 — tendenza ribassista di lungo',
    techSig.trend === 'RIBASSISTA' && 'Trend ADX direzionale al ribasso',
    quoteData?.debt_to_equity > 200 && 'Leva finanziaria elevata',
    newsData?.sentiment_summary?.global === 'negativo' && 'Prevalenza di notizie negative',
    quoteData?.beta > 1.4 && `Beta elevato (${quoteData?.beta?.toFixed(2)}) — alta volatilità`,
  ].filter(Boolean);

  const radarData = [
    { subject: 'Tecnica', value: scores.tech },
    { subject: 'Fondamentale', value: scores.fund },
    { subject: 'Sentiment', value: scores.news },
    { subject: 'Macro', value: scores.macro },
  ];

  if (!quoteData && !technicalData) return <Spinner />;

  return (
    <div>
      <Card style={{ marginBottom: 20, borderColor: `${color}40` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: `${color}18`, border: `2px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
            {icon}
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Giudizio complessivo</div>
            <div style={{ fontSize: 20, fontWeight: 700, color, letterSpacing: '0.03em' }}>{label}</div>
            <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>Punteggio composito: {scores.composite}/100</div>
          </div>
        </div>
        <ScoreBar label="Analisi tecnica (35%)"      score={scores.tech}  />
        <ScoreBar label="Analisi fondamentale (35%)" score={scores.fund}  />
        <ScoreBar label="Sentiment notizie (20%)"    score={scores.news}  />
        <ScoreBar label="Contesto macro (10%)"       score={scores.macro} />
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <Card>
          <SectionTitle>Punti di forza</SectionTitle>
          {bullish.length === 0 && <div style={{ fontSize: 13, color: '#475569' }}>Nessun segnale rialzista rilevante</div>}
          {bullish.map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid #1a2030', fontSize: 13 }}>
              <span style={{ color: '#10b981', marginTop: 1 }}>✓</span>
              <span style={{ color: '#94a3b8' }}>{b}</span>
            </div>
          ))}
        </Card>
        <Card>
          <SectionTitle>Rischi principali</SectionTitle>
          {bearish.length === 0 && <div style={{ fontSize: 13, color: '#475569' }}>Nessun segnale ribassista rilevante</div>}
          {bearish.map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid #1a2030', fontSize: 13 }}>
              <span style={{ color: '#f87171', marginTop: 1 }}>⚠</span>
              <span style={{ color: '#94a3b8' }}>{b}</span>
            </div>
          ))}
        </Card>
      </div>

      <Card>
        <SectionTitle>Profilo di rischio/rendimento</SectionTitle>
        <ResponsiveContainer width="100%" height={220}>
          <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <PolarGrid stroke="#1e2736" />
            <PolarAngleAxis dataKey="subject" tick={{ fill: '#475569', fontSize: 12 }} />
            <Radar name={ticker} dataKey="value" stroke={color} fill={color} fillOpacity={0.15} strokeWidth={1.5} />
          </RadarChart>
        </ResponsiveContainer>
        <div style={{ fontSize: 12, color: '#334155', textAlign: 'center', marginTop: 8 }}>
          Punteggi su scala 0–100. Composto: tecnica 35% · fondamentale 35% · sentiment 20% · macro 10%
        </div>
      </Card>

      <ClaudeAnalysis
        ticker={ticker}
        quoteData={quoteData}
        technicalData={technicalData}
        newsData={newsData}
        scores={scores}
      />
    </div>
  );
}
